import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  ArrowLeft,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "/renewy-logo.png";

const COUNTRIES = [
  "Ireland", "United Kingdom", "Germany", "France", "Netherlands", "Belgium",
  "Spain", "Italy", "Portugal", "Poland", "Austria", "Switzerland",
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
  "United States", "Canada", "Other"
];

const FileUploadField = ({ label, required, file, onFileChange, accept = ".pdf,.jpg,.jpeg,.png" }) => {
  const handleChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      onFileChange(selectedFile);
    }
  };

  return (
    <div>
      <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          file ? 'border-[#006932]/50 bg-[#006932]/10' : 'border-white/10 bg-black/50'
        } transition-colors`}>
          {file ? (
            <>
              <CheckCircle size={18} className="text-[#00A650] flex-shrink-0" />
              <span className="text-white text-sm truncate flex-1">{file.name}</span>
              <span className="text-xs text-zinc-500">{(file.size / 1024).toFixed(0)} KB</span>
            </>
          ) : (
            <>
              <Upload size={18} className="text-zinc-500 flex-shrink-0" />
              <span className="text-zinc-500 text-sm">Click to upload file</span>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-zinc-600 mt-1">PDF, JPG, PNG (max 10MB)</p>
    </div>
  );
};

const RegisterPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    companyRegNo: "",
    vatNumber: "",
    address: "",
    addressLine2: "",
    city: "",
    postalCode: "",
    country: "",
  });
  const [files, setFiles] = useState({
    companyReg: null,
    vatCert: null,
    proofOfAddress: null,
    tradeLicense: null,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    setFiles(prev => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required files
    if (!files.companyReg || !files.vatCert || !files.proofOfAddress) {
      toast.error("Please upload all required documents");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Add files
      if (files.companyReg) submitData.append("companyReg", files.companyReg);
      if (files.vatCert) submitData.append("vatCert", files.vatCert);
      if (files.proofOfAddress) submitData.append("proofOfAddress", files.proofOfAddress);
      if (files.tradeLicense) submitData.append("tradeLicense", files.tradeLicense);

      await axios.post(`${API}/register`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setIsSuccess(true);
      toast.success("Registration submitted successfully!");
    } catch (error) {
      console.error("Registration failed:", error);
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <motion.div
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="glass-card rounded-2xl p-8">
            <div className="w-16 h-16 rounded-full bg-[#006932]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-[#00A650]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Registration Submitted</h2>
            <p className="text-zinc-400 mb-6">
              Thank you for registering with Renewy. Our team will review your application and contact you within 2-3 business days.
            </p>
            <a href="/">
              <Button className="bg-[#006932] hover:bg-[#00A650] text-white px-6 py-3 rounded-full">
                Return to Homepage
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center justify-between py-4">
            <a href="/" className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="Renewy Logo" className="h-12 sm:h-16 w-auto" />
            </a>
            <a href="/" className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
              <ArrowLeft size={16} />
              Back to site
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Register With Us</h1>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Complete the form below to register as a trade partner. All fields marked with * are required.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#006932]/20 flex items-center justify-center">
                  <User size={20} className="text-[#00A650]" />
                </div>
                <h2 className="text-lg font-semibold text-white">Contact Information</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    placeholder="John"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-firstName"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    placeholder="Smith"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-lastName"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="john@company.com"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Phone <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="+353 ..."
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#006932]/20 flex items-center justify-center">
                  <Building2 size={20} className="text-[#00A650]" />
                </div>
                <h2 className="text-lg font-semibold text-white">Company Information</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Company Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => handleChange("companyName", e.target.value)}
                    placeholder="Your Company Ltd"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-companyName"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Company Registration No. <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.companyRegNo}
                    onChange={(e) => handleChange("companyRegNo", e.target.value)}
                    placeholder="123456789"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-companyRegNo"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    VAT Number <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.vatNumber}
                    onChange={(e) => handleChange("vatNumber", e.target.value)}
                    placeholder="IE1234567X"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-vatNumber"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#006932]/20 flex items-center justify-center">
                  <MapPin size={20} className="text-[#00A650]" />
                </div>
                <h2 className="text-lg font-semibold text-white">Business Address</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Address Line 1 <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="123 Business Street"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-address"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Address Line 2
                  </label>
                  <Input
                    value={formData.addressLine2}
                    onChange={(e) => handleChange("addressLine2", e.target.value)}
                    placeholder="Suite 100"
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-addressLine2"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    City <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="Dublin"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Postal Code <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={formData.postalCode}
                    onChange={(e) => handleChange("postalCode", e.target.value)}
                    placeholder="D01 AB12"
                    required
                    className="bg-black/50 border-white/10 focus:border-[#006932] text-white"
                    data-testid="input-postalCode"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                    Country <span className="text-red-400">*</span>
                  </label>
                  <Select value={formData.country} onValueChange={(v) => handleChange("country", v)} required>
                    <SelectTrigger className="bg-black/50 border-white/10 focus:border-[#006932] text-white" data-testid="select-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#09090b] border-white/10 max-h-60">
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Document Upload */}
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#006932]/20 flex items-center justify-center">
                  <FileText size={20} className="text-[#00A650]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Document Upload</h2>
                  <p className="text-xs text-zinc-500">Upload verification documents</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FileUploadField
                  label="Company Registration Certificate"
                  required
                  file={files.companyReg}
                  onFileChange={(f) => handleFileChange("companyReg", f)}
                />
                <FileUploadField
                  label="VAT Certificate"
                  required
                  file={files.vatCert}
                  onFileChange={(f) => handleFileChange("vatCert", f)}
                />
                <FileUploadField
                  label="Proof of Business Address"
                  required
                  file={files.proofOfAddress}
                  onFileChange={(f) => handleFileChange("proofOfAddress", f)}
                />
                <FileUploadField
                  label="Trade License (Optional)"
                  required={false}
                  file={files.tradeLicense}
                  onFileChange={(f) => handleFileChange("tradeLicense", f)}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4">
              <p className="text-xs text-zinc-500 text-center sm:text-left">
                By registering, you agree to our terms and conditions for trade partners.
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-[#006932] hover:bg-[#00A650] text-white font-semibold px-8 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(0,105,50,0.4)] disabled:opacity-50"
                data-testid="submit-register-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Registration"
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} Renewy. Renewed • Reimagined. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RegisterPage;
